import { useEffect, useMemo, useState, type ComponentType } from 'react';
import { useQuery } from '@tanstack/react-query';
import { labApi } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import {
  AlertCircle,
  CheckCircle,
  ClipboardList,
  DollarSign,
  ExternalLink,
  FileText,
  Link as LinkIcon,
  Megaphone,
  Plus,
  Save,
  Star,
  StickyNote,
  Trash2,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import type { LabPricePackage, LabProduct } from './types';

type FieldValueUpdater = unknown | ((currentValue: unknown) => unknown);

export interface LabProductFieldConfig {
  key: string;
  label: string;
  type: string;
  icon?: string;
  showInTable: boolean;
  width?: number;
  isCustom?: boolean;
  allowDuplicate?: boolean;
}

export interface LabProductFieldOption {
  value: string;
  label: string;
  color: string;
}

interface ProductSourcesPanelProps {
  product: LabProduct;
  fields: LabProductFieldConfig[];
  fieldOptions: Record<string, LabProductFieldOption[]>;
  onSave: (data: Record<string, unknown>) => Promise<void>;
  onRemoveLaboratory: () => void;
}

interface SourceGroupConfig {
  id: SourceGroupId;
  title: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
}

type SourceGroupId = 'profile' | 'offer' | 'advertising' | 'research' | 'additional';

const KNOWN_DB_KEYS = new Set([
  'name',
  'description',
  'niche',
  'potential',
  'testStatus',
  'storeId',
  'brandId',
  'competitorLinks',
  'marketplaceLink',
  'primaryMarketplaceUrl',
  'primaryWholesaleUrl',
  'otherSources',
  'adTranscripts',
  'pricePackages',
  'demographicsImage',
  'reviewsCsv',
  'scrapedContent',
  'sortOrder',
  'status',
  'imageUrl',
]);

const READONLY_FIELD_KEYS = new Set(['id', 'createdAt', 'knowledgeBases', 'contents', '_count', 'addedToLab']);
const HIDDEN_PRODUCT_FIELD_KEYS = new Set(['status']);

const DEFAULT_TEST_STATUS_OPTIONS: LabProductFieldOption[] = [
  { value: 'PENDING', label: '⏳ Oczekujący', color: '#9ca3af' },
  { value: 'TO_TEST', label: '🧪 Do testów', color: '#34d399' },
  { value: 'TESTING', label: '🔬 W trakcie testów', color: '#38bdf8' },
  { value: 'TEST_SUCCESS', label: '🏆 Udany test', color: '#4ade80' },
  { value: 'TEST_FAILED', label: '💩 Negatywny test', color: '#f87171' },
];

const POTENTIAL_OPTIONS: LabProductFieldOption[] = [
  { value: 'STANDARD', label: 'Standardowy', color: '#4ade80' },
  { value: 'HIGH', label: 'Wysoki', color: '#f97316' },
];

const LAB_STATUS_OPTIONS: LabProductFieldOption[] = [
  { value: 'DRAFT', label: 'Szkic', color: '#9ca3af' },
  { value: 'INPUT', label: 'Dane uzupełnione', color: '#4a9eff' },
  { value: 'ANALYZING', label: 'Analiza', color: '#f59e0b' },
  { value: 'KNOWLEDGE_READY', label: 'Baza wiedzy gotowa', color: '#10b981' },
  { value: 'GENERATING', label: 'Generowanie', color: '#3b82f6' },
  { value: 'COMPLETE', label: 'Kompletne', color: '#10b981' },
];

const PACKAGE_QTY_OPTIONS = ['1 sztuka', '2 sztuki', '3 sztuki', '4 sztuki', '5 sztuk', 'Inne'];

const SOURCE_GROUPS: SourceGroupConfig[] = [
  {
    id: 'profile',
    title: 'Profil produktu',
    description: 'Podstawowe dane, status testu i opis używany przy generowaniu zasobów.',
    icon: FileText,
  },
  {
    id: 'offer',
    title: 'Oferta i ceny',
    description: 'Marketplace, pakiety cenowe i informacje potrzebne do architektury oferty.',
    icon: DollarSign,
  },
  {
    id: 'advertising',
    title: 'Materiały reklamowe',
    description: 'Transkrypty, hooki i źródła kreatywne dla copy oraz reklam wideo.',
    icon: Megaphone,
  },
  {
    id: 'research',
    title: 'Źródła i research',
    description: 'Linki konkurencji, landingi, opinie, scrapowane treści i inne materiały wejściowe.',
    icon: ClipboardList,
  },
  {
    id: 'additional',
    title: 'Pola dodatkowe',
    description: 'Niestandardowe pola z konfiguracji Laboratorium, nadal zapisywane w produkcie.',
    icon: StickyNote,
  },
];

function getProductValue(product: LabProduct, key: string): unknown {
  if (KNOWN_DB_KEYS.has(key) || READONLY_FIELD_KEYS.has(key)) {
    return (product as unknown as Record<string, unknown>)[key];
  }
  return product.customFields?.[key] ?? '';
}

function getTestStatusOptions(fieldOptions: Record<string, LabProductFieldOption[]>): LabProductFieldOption[] {
  return fieldOptions.testStatus?.length ? fieldOptions.testStatus : DEFAULT_TEST_STATUS_OPTIONS;
}

function optionsForField(field: LabProductFieldConfig, fieldOptions: Record<string, LabProductFieldOption[]>): LabProductFieldOption[] {
  if (field.key === 'status') return LAB_STATUS_OPTIONS;
  if (field.type === 'potential') return POTENTIAL_OPTIONS;
  if (field.type === 'testStatus') return getTestStatusOptions(fieldOptions);
  return fieldOptions[field.key] || [];
}

function canDuplicateField(field: LabProductFieldConfig): boolean {
  if (!field.allowDuplicate) return false;
  if (field.key === 'ads' || field.key === 'broadcast') return false;
  return true;
}

function hasInitialDuplicates(product: LabProduct): boolean {
  const duplicates = product.customFields?._fieldDuplicates;
  return Boolean(duplicates && typeof duplicates === 'object' && Object.keys(duplicates as Record<string, unknown>).length > 0);
}

function getFieldGroupId(field: LabProductFieldConfig): SourceGroupId {
  const key = field.key.toLowerCase();
  const label = field.label.toLowerCase();
  const text = `${key} ${label}`;

  if (['name', 'description', 'niche', 'potential', 'teststatus', 'status', 'storeid', 'brandid', 'imageurl'].includes(key)) {
    return 'profile';
  }
  if (key === 'pricepackages' || text.includes('cena') || text.includes('price') || text.includes('pakiet') || text.includes('oferta')) {
    return 'offer';
  }
  if (
    key === 'adtranscripts'
    || text.includes('reklam')
    || text.includes('ad ')
    || text.includes('ads')
    || text.includes('ugc')
    || text.includes('video')
    || text.includes('kreac')
    || text.includes('hook')
  ) {
    return 'advertising';
  }
  if (
    ['competitorlinks', 'marketplacelink', 'othersources', 'reviewscsv', 'scrapedcontent', 'demographicsimage'].includes(key)
    || text.includes('konkur')
    || text.includes('marketplace')
    || text.includes('landing')
    || text.includes('advertorial')
    || text.includes('spy')
    || text.includes('hurtown')
    || text.includes('review')
    || text.includes('opini')
    || text.includes('scrap')
    || text.includes('źród')
    || text.includes('zrod')
    || text.includes('link')
  ) {
    return 'research';
  }
  return 'additional';
}

function visibleFields(fields: LabProductFieldConfig[]): LabProductFieldConfig[] {
  const statusField = fields.find(field => field.key === 'status');
  const testStatusField = fields.find(field => field.key === 'testStatus');
  const visible = fields.filter(field =>
    !READONLY_FIELD_KEYS.has(field.key)
    && !HIDDEN_PRODUCT_FIELD_KEYS.has(field.key)
    && field.key !== 'customFields'
    && field.key !== 'testStatus'
  );

  // Brand assignment is available even with an older system field configuration.
  if (!visible.some(field => field.key === 'brandId')) {
    visible.push({ key: 'brandId', label: 'Marka', type: 'select', showInTable: false });
  }

  if (!testStatusField) return visible;

  const normalizedTestStatus = {
    ...testStatusField,
    label: testStatusField.label || 'Status',
    showInTable: testStatusField.showInTable || Boolean(statusField?.showInTable),
  };
  const insertAt = statusField ? fields.findIndex(field => field.key === 'status') : fields.findIndex(field => field.key === 'testStatus');
  visible.splice(Math.max(0, Math.min(insertAt, visible.length)), 0, normalizedTestStatus);
  return visible;
}

function stringifyInitialValue(value: unknown): unknown {
  if (value == null) return '';
  return value;
}

function fieldValueAsString(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

function isAbsoluteUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

type PrimarySourceKey = 'primaryMarketplaceUrl' | 'primaryWholesaleUrl';

function primaryKeyForField(fieldKey: string): PrimarySourceKey | null {
  if (fieldKey === 'marketplaceLink') return 'primaryMarketplaceUrl';
  if (fieldKey === 'wholesale') return 'primaryWholesaleUrl';
  return null;
}

function normalizeUrlIdentity(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    const url = new URL(value.trim());
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    url.hash = '';
    return url.toString();
  } catch {
    return null;
  }
}

function resolvePrimaryUrl(selected: unknown, candidates: unknown[]): string | null {
  const urls = [...new Set(candidates.map(normalizeUrlIdentity).filter((url): url is string => Boolean(url)))];
  const normalizedSelected = normalizeUrlIdentity(selected);
  return normalizedSelected && urls.includes(normalizedSelected) ? normalizedSelected : urls[0] || null;
}

function normalizeForSave(field: LabProductFieldConfig, value: unknown): unknown {
  if (field.key === 'brandId') return value || null;
  if (field.type === 'number') {
    if (value === '' || value == null) return undefined;
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : value;
  }
  return value;
}

export function ProductSourcesPanel({
  product,
  fields,
  fieldOptions,
  onSave,
  onRemoveLaboratory,
}: ProductSourcesPanelProps) {
  const tenantId = useAuthStore(state => state.tenant?.id);
  const brandsQuery = useQuery<Array<{ id: string; name: string }>>({
    queryKey: ['lab-brands', tenantId],
    queryFn: async () => (await labApi.getBrands()).data.data?.brands || [],
    enabled: Boolean(tenantId),
    staleTime: 30_000,
  });
  const [form, setForm] = useState<Record<string, unknown>>({});
  const [duplicatedFields, setDuplicatedFields] = useState<Record<string, string[]>>({});
  const [primaryUrls, setPrimaryUrls] = useState<Record<PrimarySourceKey, string | null>>({
    primaryMarketplaceUrl: null,
    primaryWholesaleUrl: null,
  });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const laboratoryBusy = product.status === 'ANALYZING'
    || product.status === 'GENERATING'
    || product.contents?.some(content => content.status === 'GENERATING');

  const editableFields = useMemo(() => visibleFields(fields), [fields]);
  const groupedFields = useMemo(() => {
    const map = new Map<SourceGroupId, LabProductFieldConfig[]>();
    for (const group of SOURCE_GROUPS) map.set(group.id, []);
    for (const field of editableFields) {
      const groupId = getFieldGroupId(field);
      map.set(groupId, [...(map.get(groupId) || []), field]);
    }
    return SOURCE_GROUPS.map(group => ({
      ...group,
      fields: map.get(group.id) || [],
    })).filter(group => group.fields.length > 0);
  }, [editableFields]);

  useEffect(() => {
    const initial: Record<string, unknown> = {};
    for (const field of editableFields) {
      initial[field.key] = stringifyInitialValue(getProductValue(product, field.key));
    }
    const duplicates = product.customFields?._fieldDuplicates;
    const initialDuplicates = duplicates && typeof duplicates === 'object'
      ? duplicates as Record<string, string[]>
      : {};
    setForm(initial);
    setDuplicatedFields(initialDuplicates);
    setPrimaryUrls({
      primaryMarketplaceUrl: resolvePrimaryUrl(product.primaryMarketplaceUrl, [
        product.marketplaceLink,
        ...(initialDuplicates.marketplaceLink || []),
      ]),
      primaryWholesaleUrl: resolvePrimaryUrl(product.primaryWholesaleUrl, [
        product.customFields?.wholesale,
        ...(initialDuplicates.wholesale || []),
      ]),
    });
    setDirty(false);
    setSaveStatus('idle');
  }, [product, editableFields]);

  const updateField = (key: string, value: FieldValueUpdater) => {
    setForm(prev => {
      const nextValue = typeof value === 'function'
        ? (value as (currentValue: unknown) => unknown)(prev[key])
        : value;
      return { ...prev, [key]: nextValue };
    });
    setDirty(true);
    setSaveStatus('idle');
  };

  const addDuplicate = (fieldKey: string) => {
    setDuplicatedFields(prev => ({
      ...prev,
      [fieldKey]: [...(prev[fieldKey] || []), ''],
    }));
    setDirty(true);
    setSaveStatus('idle');
  };

  const updateDuplicate = (fieldKey: string, index: number, value: string) => {
    const primaryKey = primaryKeyForField(fieldKey);
    const previousValue = duplicatedFields[fieldKey]?.[index];
    setDuplicatedFields(prev => ({
      ...prev,
      [fieldKey]: (prev[fieldKey] || []).map((item, i) => i === index ? value : item),
    }));
    if (primaryKey && normalizeUrlIdentity(previousValue) === primaryUrls[primaryKey]) {
      setPrimaryUrls(prev => ({ ...prev, [primaryKey]: normalizeUrlIdentity(value) }));
    }
    setDirty(true);
    setSaveStatus('idle');
  };

  const removeDuplicate = (fieldKey: string, index: number) => {
    const primaryKey = primaryKeyForField(fieldKey);
    const removedValue = duplicatedFields[fieldKey]?.[index];
    const remaining = (duplicatedFields[fieldKey] || []).filter((_, i) => i !== index);
    setDuplicatedFields(prev => ({
      ...prev,
      [fieldKey]: (prev[fieldKey] || []).filter((_, i) => i !== index),
    }));
    if (primaryKey && normalizeUrlIdentity(removedValue) === primaryUrls[primaryKey]) {
      setPrimaryUrls(prev => ({
        ...prev,
        [primaryKey]: resolvePrimaryUrl(null, [form[fieldKey], ...remaining]),
      }));
    }
    setDirty(true);
    setSaveStatus('idle');
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus('idle');
    try {
      const data: Record<string, unknown> = {};
      for (const field of editableFields) {
        const normalized = normalizeForSave(field, form[field.key]);
        if (normalized !== undefined) data[field.key] = normalized;
      }
      if (Object.keys(duplicatedFields).length > 0 || hasInitialDuplicates(product)) {
        data.customFields = { _fieldDuplicates: duplicatedFields };
      }
      data.primaryMarketplaceUrl = resolvePrimaryUrl(primaryUrls.primaryMarketplaceUrl, [
        form.marketplaceLink,
        ...(duplicatedFields.marketplaceLink || []),
      ]);
      data.primaryWholesaleUrl = resolvePrimaryUrl(primaryUrls.primaryWholesaleUrl, [
        form.wholesale,
        ...(duplicatedFields.wholesale || []),
      ]);
      await onSave(data);
      setDirty(false);
      setSaveStatus('success');
      window.setTimeout(() => setSaveStatus('idle'), 2500);
    } catch (error) {
      console.error('Nie udało się zapisać źródeł produktu:', error);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  const renderSelectField = (field: LabProductFieldConfig, value: unknown) => {
    const options = optionsForField(field, fieldOptions);
    return (
      <Select
        value={fieldValueAsString(value)}
        onChange={event => updateField(field.key, event.target.value)}
        placeholder="Wybierz"
      >
        <option value="">Brak</option>
        {options.map(option => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </Select>
    );
  };

  const renderPrimaryButton = (fieldKey: string, value: string) => {
    const primaryKey = primaryKeyForField(fieldKey);
    if (!primaryKey) return null;
    const normalized = normalizeUrlIdentity(value);
    const selected = Boolean(normalized && normalized === primaryUrls[primaryKey]);
    return (
      <button
        type="button"
        disabled={!normalized}
        onClick={() => {
          setPrimaryUrls(prev => ({ ...prev, [primaryKey]: normalized }));
          setDirty(true);
          setSaveStatus('idle');
        }}
        className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-input border transition-colors ${selected
          ? 'border-warning/50 bg-warning/10 text-warning'
          : 'border-border bg-surface-base text-text-muted hover:border-warning/40 hover:text-warning'} disabled:cursor-not-allowed disabled:opacity-40`}
        aria-label={selected ? 'To jest główne źródło' : 'Ustaw jako główne źródło'}
        aria-pressed={selected}
        title={selected ? 'Główne źródło używane przez AI' : 'Ustaw jako główne źródło używane przez AI'}
      >
        <Star className={`h-4 w-4 ${selected ? 'fill-current' : ''}`} />
      </button>
    );
  };

  const renderUrlField = (field: LabProductFieldConfig, value: unknown, placeholder = 'https://...') => {
    const urlValue = fieldValueAsString(value);
    return (
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Input
            value={urlValue}
            onChange={event => {
              const primaryKey = primaryKeyForField(field.key);
              if (primaryKey && normalizeUrlIdentity(urlValue) === primaryUrls[primaryKey]) {
                setPrimaryUrls(prev => ({ ...prev, [primaryKey]: normalizeUrlIdentity(event.target.value) }));
              }
              updateField(field.key, event.target.value);
            }}
            placeholder={placeholder}
            className={urlValue ? 'pr-10' : undefined}
            leftIcon={<LinkIcon />}
          />
          {isAbsoluteUrl(urlValue) && (
            <a
              href={urlValue}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-accent-primary transition-colors"
              aria-label={`Otwórz ${field.label}`}
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>
        {renderPrimaryButton(field.key, urlValue)}
      </div>
    );
  };

  const renderTranscriptsField = (field: LabProductFieldConfig) => {
    const transcripts = Array.isArray(form[field.key])
      ? form[field.key] as string[]
      : [];
    const updateTranscript = (index: number, value: string) => {
      updateField(field.key, (currentValue: unknown) => {
        const current = Array.isArray(currentValue) ? currentValue as string[] : [];
        const source = current.length > 0 ? current : [''];
        return source.map((item, i) => i === index ? value : item);
      });
    };
    const visibleTranscripts = transcripts.length > 0 ? transcripts : [''];

    return (
      <div className="space-y-3">
        {visibleTranscripts.map((transcript, index) => (
          <div key={`${field.key}-${index}`} className="flex items-start gap-2">
            <Textarea
              value={transcript}
              onChange={event => updateTranscript(index, event.target.value)}
              rows={6}
              placeholder={`Transkrypt reklamy ${index + 1}`}
            />
            {transcripts.length > 0 && (
              <Button
                variant="icon"
                size="sm"
                onClick={() => updateField(field.key, (currentValue: unknown) => {
                  const current = Array.isArray(currentValue) && currentValue.length > 0
                    ? currentValue as string[]
                    : [''];
                  const next = current.filter((_, i) => i !== index);
                  return next.length > 0 ? next : [''];
                })}
                aria-label="Usuń transkrypt"
                leftIcon={<X />}
                className="mt-1"
              />
            )}
          </div>
        ))}
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Plus />}
          onClick={() => updateField(field.key, (currentValue: unknown) => {
            const current = Array.isArray(currentValue) ? currentValue as string[] : [];
            const source = current.length > 0 ? current : [''];
            return [...source, ''];
          })}
        >
          Dodaj transkrypt
        </Button>
      </div>
    );
  };

  const renderPackagesField = (field: LabProductFieldConfig) => {
    const packages = Array.isArray(form[field.key])
      ? form[field.key] as LabPricePackage[]
      : [];

    const updatePackage = (index: number, key: keyof LabPricePackage, value: string) => {
      updateField(field.key, packages.map((item, i) => i === index ? { ...item, [key]: value } : item));
    };

    return (
      <div className="space-y-3">
        {packages.length === 0 && (
          <div className="rounded-card border border-dashed border-border bg-surface-base/30 p-4 text-sm text-text-muted">
            Brak pakietów cenowych. Dodaj warianty, które mają trafić do oferty.
          </div>
        )}
        {packages.map((pkg, index) => {
          const isCustomName = pkg.name && !PACKAGE_QTY_OPTIONS.includes(pkg.name);
          const selectValue = isCustomName ? 'Inne' : pkg.name;
          return (
            <div key={index} className="rounded-card border border-border-subtle bg-surface-base/30 p-3 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-[1.2fr_1fr_1fr_auto] gap-3 items-end">
                <FormField label="Pakiet">
                  <Select
                    value={selectValue || ''}
                    onChange={event => updatePackage(index, 'name', event.target.value === 'Inne' ? '' : event.target.value)}
                  >
                    <option value="">Wybierz ilość</option>
                    {PACKAGE_QTY_OPTIONS.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </Select>
                </FormField>
                <FormField label="Stara cena">
                  <Input
                    value={pkg.oldPrice}
                    onChange={event => updatePackage(index, 'oldPrice', event.target.value)}
                    placeholder="np. 199 zł"
                  />
                </FormField>
                <FormField label="Nowa cena">
                  <Input
                    value={pkg.newPrice}
                    onChange={event => updatePackage(index, 'newPrice', event.target.value)}
                    placeholder="np. 99 zł"
                  />
                </FormField>
                <Button
                  variant="icon"
                  size="md"
                  onClick={() => updateField(field.key, packages.filter((_, i) => i !== index))}
                  aria-label="Usuń pakiet"
                  leftIcon={<Trash2 />}
                />
              </div>
              {(selectValue === 'Inne' || isCustomName) && (
                <FormField label="Własna nazwa pakietu">
                  <Input
                    value={pkg.name}
                    onChange={event => updatePackage(index, 'name', event.target.value)}
                    placeholder="np. Zestaw rodzinny"
                  />
                </FormField>
              )}
            </div>
          );
        })}
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Plus />}
          onClick={() => updateField(field.key, [...packages, { name: '1 sztuka', oldPrice: '', newPrice: '' }])}
        >
          Dodaj pakiet
        </Button>
      </div>
    );
  };

  const renderFieldControl = (field: LabProductFieldConfig) => {
    const value = form[field.key];

    if (field.key === 'brandId') {
      const brands = brandsQuery.data || [];
      return (
        <div className="space-y-2">
          <Select
            aria-label="Marka produktu"
            value={fieldValueAsString(value)}
            disabled={brandsQuery.isPending || brandsQuery.isError}
            onChange={event => updateField(field.key, event.target.value || null)}
          >
            <option value="">Bez marki</option>
            {product.brand && !brands.some(brand => brand.id === product.brandId) && (
              <option value={product.brand.id}>{product.brand.name}</option>
            )}
            {brands.map(brand => <option key={brand.id} value={brand.id}>{brand.name}</option>)}
          </Select>
          {brandsQuery.isPending && <p className="text-xs text-text-muted">Wczytywanie marek…</p>}
          {brandsQuery.isError && (
            <div className="flex items-center gap-2 text-xs text-danger">
              <span>Nie udało się wczytać marek.</span>
              <Button variant="ghost" size="sm" onClick={() => void brandsQuery.refetch()}>Spróbuj ponownie</Button>
            </div>
          )}
        </div>
      );
    }

    if (field.type === 'select' || field.type === 'potential' || field.type === 'testStatus' || field.key === 'status') {
      return renderSelectField(field, value);
    }
    if (field.type === 'textarea') {
      return (
        <Textarea
          value={fieldValueAsString(value)}
          onChange={event => updateField(field.key, event.target.value)}
          minRows={field.key === 'description' ? 5 : 3}
          maxRows={14}
          autoResize
        />
      );
    }
    if (field.type === 'url') {
      return renderUrlField(field, value);
    }
    if (field.type === 'number') {
      return (
        <Input
          type="number"
          value={fieldValueAsString(value)}
          onChange={event => updateField(field.key, event.target.value)}
          placeholder="0"
        />
      );
    }
    if (field.type === 'transcripts') {
      return renderTranscriptsField(field);
    }
    if (field.type === 'packages') {
      return renderPackagesField(field);
    }
    if (field.type === 'date') {
      return (
        <div className="rounded-input border border-border bg-surface-base/40 px-field-x py-field-y text-sm text-text-muted">
          {fieldValueAsString(value) || 'Brak daty'}
        </div>
      );
    }
    return (
      <Input
        value={fieldValueAsString(value)}
        onChange={event => updateField(field.key, event.target.value)}
      />
    );
  };

  if (editableFields.length === 0) {
    return (
      <Card padding="lg" className="text-center">
        <AlertCircle className="w-8 h-8 text-text-muted mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-text-primary">Brak skonfigurowanych pól</h3>
        <p className="text-sm text-text-muted mt-1">
          Pola produktu nie zostały załadowane z konfiguracji Laboratorium.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {groupedFields.map(group => {
          const Icon = group.icon;
          const wide = group.id === 'advertising' || group.id === 'research' || group.id === 'additional';
          return (
            <Card
              key={group.id}
              padding="md"
              className={wide ? 'xl:col-span-2' : undefined}
              header={(
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-accent-primary/10 border border-accent-primary/20 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-accent-primary" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-text-primary">{group.title}</h3>
                    <p className="text-sm text-text-muted mt-1">{group.description}</p>
                  </div>
                </div>
              )}
            >
              <div className={group.id === 'profile' || group.id === 'offer' ? 'space-y-4' : 'grid grid-cols-1 lg:grid-cols-2 gap-4'}>
                {group.fields.map(field => {
                  const fullWidth = ['textarea', 'transcripts', 'packages'].includes(field.type) || canDuplicateField(field);
                  return (
                    <div key={field.key} className={fullWidth ? 'lg:col-span-2' : undefined}>
                      <FormField label={field.label}>
                        {renderFieldControl(field)}
                      </FormField>
                      {canDuplicateField(field) && (
                        <div className="space-y-2 mt-2">
                          {(duplicatedFields[field.key] || []).map((value, index) => (
                            <div key={index} className="flex items-center gap-2">
                              <div className="relative flex-1">
                                <Input
                                  value={value}
                                  onChange={event => updateDuplicate(field.key, index, event.target.value)}
                                  placeholder={`${field.label} ${index + 2}`}
                                  className={field.type === 'url' && isAbsoluteUrl(value) ? 'pr-10' : undefined}
                                  leftIcon={field.type === 'url' ? <LinkIcon /> : undefined}
                                />
                                {field.type === 'url' && isAbsoluteUrl(value) && (
                                  <a
                                    href={value}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-accent-primary transition-colors"
                                    aria-label={`Otwórz dodatkowe pole ${field.label}`}
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                  </a>
                                )}
                              </div>
                              {field.type === 'url' && renderPrimaryButton(field.key, value)}
                              <Button
                                variant="icon"
                                size="sm"
                                onClick={() => removeDuplicate(field.key, index)}
                                aria-label={`Usuń dodatkowe pole ${field.label}`}
                                leftIcon={<X />}
                              />
                            </div>
                          ))}
                          <Button
                            variant="ghost"
                            size="sm"
                            leftIcon={<Plus />}
                            onClick={() => addDuplicate(field.key)}
                          >
                            Dodaj kolejne pole
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          );
        })}
      </div>

      <Card padding="none" className="border-danger/30 bg-danger/[0.04]">
        <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="max-w-2xl">
            <h3 className="text-sm font-semibold text-danger">Strefa niebezpieczna</h3>
            <p className="mt-1 text-xs leading-relaxed text-text-muted">
              Usuń bazy wiedzy, treści i zasoby tego Laboratorium. Produkt pozostanie w Bazie produktów, a pliki na Google Drive nie zostaną skasowane.
            </p>
            {laboratoryBusy && (
              <p className="mt-2 text-xs font-medium text-warning">
                Najpierw zatrzymaj aktywny import lub generowanie.
              </p>
            )}
          </div>
          <Button
            variant="danger"
            size="sm"
            leftIcon={<Trash2 />}
            disabled={laboratoryBusy}
            onClick={onRemoveLaboratory}
          >
            Usuń Laboratorium
          </Button>
        </div>
      </Card>

      <div className="sticky bottom-0 z-20 pt-2">
        <div className="rounded-card border border-border bg-surface-raised/95 backdrop-blur-xl shadow-glass p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="primary"
              leftIcon={<Save />}
              loading={saving}
              disabled={!dirty || saving}
              onClick={handleSave}
            >
              Zapisz źródła
            </Button>
            {!dirty && saveStatus === 'idle' && (
              <span className="text-xs text-text-muted">Brak niezapisanych zmian</span>
            )}
            {dirty && !saving && saveStatus === 'idle' && (
              <span className="text-xs text-warning">Masz niezapisane zmiany</span>
            )}
            {saveStatus === 'success' && (
              <span className="inline-flex items-center gap-1.5 text-xs text-success">
                <CheckCircle className="w-3.5 h-3.5" />
                Zapisano
              </span>
            )}
            {saveStatus === 'error' && (
              <span className="inline-flex items-center gap-1.5 text-xs text-danger">
                <AlertCircle className="w-3.5 h-3.5" />
                Błąd zapisu
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
